import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BadgePercent, MessageCircle, ArrowRight, Download, ShieldCheck, Zap, Award, Sun, Battery, Cpu, Plug, BadgeCheck, Factory, Car, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import TrustBadges from "@/components/TrustBadges";
import { setSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG = "/images/feston";
const WHATSAPP = "918605657016";
const wa = (msg) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

const TABS = [
  { key: "all", label: "All Feston" },
  { key: "ongrid", label: "On-Grid Inverters", test: (p) => p.category === "On Grid Inverter" },
  { key: "hybrid", label: "Hybrid Inverters", test: (p) => p.category === "Hybrid Inverter" },
  { key: "micro", label: "Micro Inverters", test: (p) => p.category === "Micro Inverter" },
  { key: "battery", label: "Batteries", test: (p) => p.category === "Battery" },
];

const RANGE = [
  { tab: "ongrid", icon: Sun, title: "On-Grid Inverters", sub: "1.5 kW - 136 kW, single & three phase", img: `${IMG}/ongrid-3ph-15-25kw.jpg` },
  { tab: "hybrid", icon: Zap, title: "Hybrid Inverters", sub: "3 kW - 80 kW, low & high voltage", img: `${IMG}/hybrid-1ph-3-6kw.jpg` },
  { tab: "micro", icon: Cpu, title: "Micro Inverters", sub: "1 kW & 2.2 kW, module-level MPPT", img: `${IMG}/micro-2.2kw-4mppt.jpg` },
  { tab: "battery", icon: Battery, title: "LFP Batteries", sub: "5.12, 11.8 & 16 kWh, 10-yr warranty", img: `${IMG}/battery-12k-lvb.jpg` },
];

const WHY = [
  { icon: BadgeCheck, title: "BIS, BEE & IEC certified", text: "Feston inverters carry BIS and IEC certification and BEE listing for Indian grid compliance." },
  { icon: ShieldCheck, title: "Strong warranties", text: "10 years on on-grid inverters and LFP batteries, 5-10 years on hybrids, 8-10 years on micro inverters." },
  { icon: Zap, title: "Efficient & smart", text: "Up to 98.8% efficiency, intelligent MPPT and remote monitoring through the Feston RMS app." },
  { icon: Factory, title: "Service across India", text: "FESTON S.E.V, Chennai - 10,000+ installations, 50 MW+ generated and 500+ business clients." },
];

const BANNERS = [
  { tab: "hybrid", img: `${IMG}/life-family.jpg`, title: "Backup that just works", text: "Hybrid inverters keep your home running through power cuts." },
  { tab: "ongrid", img: `${IMG}/life-ci.jpg`, title: "Built for C&I rooftops", text: "Three phase on-grid up to 136 kW with multiple MPPTs." },
  { tab: "micro", img: `${IMG}/life-micro.jpg`, title: "Micro inverters beat shade", text: "Every panel tracked on its own, IP67 rated." },
  { tab: "battery", img: `${IMG}/life-battery-home.jpg`, title: "LiFePO4 home storage", text: "6000+ cycles, wall or floor mounted, 10-year warranty." },
];

const ON_REQUEST = [
  { icon: Server, title: "BESS - Ener X / Ener X Pro", text: "100-125 kW PCS with 215-241 kWh battery cabinets for commercial & utility storage." },
  { icon: Battery, title: "FERRO HV battery systems", text: "High-voltage LFP racks, 23 kWh to 78 kWh usable, for HV hybrid inverters." },
  { icon: Car, title: "EV chargers", text: "Feston AC / DC EV charging for homes, offices and fleets." },
  { icon: Plug, title: "More sizes", text: "6.2 kW 1-ph & 4 kW 3-ph on-grid, 35-135 kW C&I, 125 kW hybrid, 0.6-2 kW micro & micro-hybrid, 2.5 / 5 kWh rack batteries." },
];

const SHEETS = [
  ["On-grid 1-phase 1.5-6.2 kW", "feston-ongrid-1ph-1.5-6.2kw.pdf"], ["On-grid 3-phase 4-25 kW", "feston-ongrid-3ph-4-25kw.pdf"],
  ["On-grid 3-phase 30-50 kW", "feston-ongrid-3ph-30-50kw.pdf"], ["On-grid 3-phase 60-80 kW", "feston-ongrid-3ph-60-80kw.pdf"],
  ["On-grid 3-phase 70-110 kW", "feston-ongrid-3ph-70-110kw.pdf"], ["On-grid 3-phase 120-136 kW", "feston-ongrid-3ph-120-136kw.pdf"],
  ["Hybrid 1-phase 3-6 kW", "feston-hybrid-1ph-3-5.5kw.pdf"], ["Hybrid 3-phase 3-12 kW", "feston-hybrid-12kw.pdf"],
  ["Hybrid 3-phase 14-20 kW", "feston-hybrid-20kw.pdf"], ["Hybrid HV 29.9-50 kW", "feston-hybrid-40-50kw.pdf"],
  ["Hybrid HV 60-80 kW", "feston-hybrid-60-80kw.pdf"], ["Hybrid HV 125 kW", "feston-hybrid-125kw.pdf"],
  ["Micro 0.6-1 kW", "feston-micro-0.6-1kw.pdf"], ["Micro 1.3-2.2 kW", "feston-micro-1.3-2.2kw.pdf"],
  ["Micro hybrid 1 kW", "feston-micro-hybrid-1-2kw.pdf"], ["Battery 5.12 kWh LVB", "feston-battery-fe-5k-lvb.pdf"],
  ["Battery 11.8 kWh LVB", "feston-battery-fe-12k-lvb.pdf"], ["Battery 16 kWh LVB", "feston-battery-fe-16k-lvb.pdf"],
  ["Battery 5 kWh LVC", "feston-battery-fes-5k-lvc.pdf"], ["Battery 5 kWh LVD", "feston-battery-fes-5k-lvd.pdf"],
  ["Battery 2.5 kWh LVC", "feston-battery-fes-2.5k-lvc.pdf"], ["FERRO HV battery", "feston-battery-ferro-hv.pdf"],
];

export default function FestonPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setSEO({
      title: "Feston Solar Inverters & LFP Batteries - Flat 4% OFF",
      description: "Buy Feston on-grid (1.5-136 kW), hybrid (3-80 kW) and micro solar inverters and LiFePO4 batteries at 4% off list price. BIS, BEE & IEC certified, datasheets, delivery across India from Glannu Solar Store.",
      canonical: "https://solar.glannu.com/brands/feston",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Brand",
        name: "Feston",
        url: "https://solar.glannu.com/brands/feston",
        logo: "https://solar.glannu.com/images/feston/hybrid-1ph-3-6kw.jpg",
        description: "FESTON S.E.V Pvt Ltd, Chennai - solar on-grid, hybrid and micro inverters, LFP batteries, BESS and EV chargers.",
      },
    });
  }, []);

  useEffect(() => {
    let live = true;
    const load = () => axios.get(`${API}/products`, { params: { brand: "Feston", limit: 200, sort: "featured" } })
      .then(({ data }) => { if (live) setProducts((data.products || []).filter((p) => !p.deleted)); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    load();
    window.addEventListener("glannu:data-updated", load);
    return () => { live = false; window.removeEventListener("glannu:data-updated", load); };
  }, []);

  const shown = useMemo(() => {
    const t = TABS.find((x) => x.key === tab);
    return t && t.test ? products.filter(t.test) : products;
  }, [products, tab]);

  const pick = (key) => {
    setTab(key);
    const el = document.getElementById("feston-products");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <nav className="flex items-center gap-2 text-sm text-neutral-500 mb-5">
        <Link to="/" className="hover:text-brand-primary">Home</Link>
        <span>/</span><span className="text-neutral-900 font-medium">Feston</span>
      </nav>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center rounded-2xl bg-gradient-to-br from-sky-50 via-white to-blue-50 border border-sky-100 p-5 sm:p-8 mb-8" data-testid="feston-hero">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white text-xs font-bold px-3 py-1 mb-4">
            <BadgePercent className="h-3.5 w-3.5" /> New brand - Flat 4% OFF list price
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-3">
            Feston solar inverters &amp; batteries, <span className="text-blue-700">always on</span>
          </h1>
          <p className="text-neutral-600 text-sm sm:text-base leading-relaxed mb-6 max-w-xl">
            On-grid, hybrid and micro inverters from 1.5 kW to 136 kW plus LiFePO4 batteries from FESTON S.E.V, Chennai - BIS, BEE &amp; IEC
            certified and now at Glannu with <strong>4% off every Feston list price</strong>, GST invoice and delivery across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => pick("all")} className="bg-brand-primary hover:bg-brand-primary-hover text-white font-semibold px-6 py-5" data-testid="feston-shop">
              Shop Feston <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a href={wa("Hi Glannu, I want a quote for Feston inverters / batteries.")} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full border-brand-whatsapp text-brand-whatsapp hover:bg-brand-whatsapp hover:text-white font-semibold px-6 py-5">
                <MessageCircle className="h-4 w-4 mr-2" /> Get a Feston quote
              </Button>
            </a>
          </div>
        </div>
        <img src={`${IMG}/life-hybrid-home.jpg`} alt="Feston solar inverter and battery at home" className="w-full rounded-xl shadow-lg" />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        {[["10,000+", "installations"], ["50 MW+", "power generated"], ["1.5 - 136 kW", "inverter range"], ["Up to 10 yrs", "manufacturer warranty"]].map(([a, b]) => (
          <div key={a} className="rounded-xl border border-neutral-200 bg-white p-4 text-center">
            <p className="font-heading text-xl sm:text-2xl font-bold text-blue-700">{a}</p>
            <p className="text-xs sm:text-sm text-neutral-500">{b}</p>
          </div>
        ))}
      </section>

      <section className="mb-10">
        <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-4">The Feston range</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {RANGE.map(({ tab: t, icon: Icon, title, sub, img }) => (
            <button key={t} onClick={() => pick(t)} className="group text-left rounded-xl border border-neutral-200 bg-white overflow-hidden hover:border-brand-primary hover:shadow-md transition-all">
              <div className="aspect-[4/3] bg-neutral-50 flex items-center justify-center p-3">
                <img src={img} alt={`Feston ${title}`} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="font-semibold text-sm text-neutral-900 flex items-center gap-1.5"><Icon className="h-4 w-4 text-brand-primary" /> {title}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{sub}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section id="feston-products" className="mb-12 scroll-mt-24">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900">Feston products <span className="text-sm font-normal text-neutral-400">({shown.length})</span></h2>
          <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-full px-3 py-1">4% OFF applied on every item</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`feston-tab-${t.key}`}
              className={`flex-shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${tab === t.key ? "bg-brand-primary text-white border-brand-primary" : "border-neutral-200 text-neutral-700 hover:border-neutral-300"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {[...Array(8)].map((_, i) => <ProductCard key={i} loading />)}
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
            Feston products are being added. Message us on WhatsApp for today's prices.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="feston-grid">
            {shown.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      <section className="mb-12 rounded-2xl border border-blue-100 bg-blue-50/50 p-5 sm:p-6" data-testid="feston-on-request">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900">Also available on request</h2>
            <p className="text-sm text-neutral-600">More of the Feston range - ask us for availability and the best price.</p>
          </div>
          <a href={wa("Hi Glannu, please share price & availability for Feston BESS / EV charger / other models.")} target="_blank" rel="noopener noreferrer">
            <Button className="bg-brand-whatsapp hover:brightness-110 text-white font-semibold"><MessageCircle className="h-4 w-4 mr-2" /> Ask on WhatsApp</Button>
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ON_REQUEST.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-3 rounded-xl bg-white border border-neutral-200 p-4">
              <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-blue-50 flex items-center justify-center"><Icon className="h-5 w-5 text-blue-700" /></div>
              <div><p className="font-semibold text-neutral-900 text-sm">{title}</p><p className="text-sm text-neutral-600">{text}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        {BANNERS.map((b) => (
          <button key={b.tab} onClick={() => pick(b.tab)} className="relative rounded-2xl overflow-hidden text-left group h-48 sm:h-56">
            <img src={b.img} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-4 sm:p-5">
              <p className="text-white font-heading font-semibold text-lg">{b.title}</p>
              <p className="text-white/80 text-sm">{b.text}</p>
            </div>
          </button>
        ))}
      </section>

      <section className="mb-12">
        <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-4">Why choose Feston?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-xl border border-neutral-200 bg-white p-5">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-blue-700" /></div>
              <p className="font-semibold text-neutral-900 mb-1">{title}</p>
              <p className="text-sm text-neutral-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12" data-testid="feston-datasheets">
        <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-4">Feston datasheets</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SHEETS.map(([label, file]) => (
            <a key={file} href={`/datasheets/${file}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-700 hover:border-brand-primary hover:text-brand-primary transition-colors">
              <Download className="h-4 w-4 flex-shrink-0" /> {label}
            </a>
          ))}
        </div>
      </section>

      <section className="rounded-2xl bg-blue-800 text-white p-6 sm:p-8 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div>
          <p className="font-heading text-xl sm:text-2xl font-bold mb-1">Planning a Feston system?</p>
          <p className="text-white/80 text-sm max-w-xl">Tell us your load or roof size - we'll suggest the right Feston inverter, battery and protection, with 4% off list price and freight to your site.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={wa("Hi Glannu, please suggest a Feston solar system for me.")} target="_blank" rel="noopener noreferrer">
            <Button className="w-full bg-brand-whatsapp hover:brightness-110 text-white font-semibold px-6 py-5"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp us</Button>
          </a>
          <Link to="/calculator">
            <Button variant="outline" className="w-full bg-transparent border-white text-white hover:bg-white hover:text-blue-800 font-semibold px-6 py-5"><Award className="h-4 w-4 mr-2" /> Size my system</Button>
          </Link>
        </div>
      </section>

      <TrustBadges className="mb-6" />
    </main>
  );
}
